import { IAppContract } from "@features/app/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { PromptsLibraryApi } from "@features/shared/api/prompts-library";
import {
    IPrompt,
    IPromptsLibraryCategory,
    IPromptsLibrarySubcategory
} from "@features/shared/entities/prompts-library";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createStore, sample } from "effector";
import { reset } from "patronum";
import { PERSONAL_BOOST_CATEGORY_IDS, ULTIMATE_AI_CATEGORY_IDS } from "../helpers";
import { IRouterContract } from "@features/shared/router";

const $allCategories = createStore<IPromptsLibraryCategory[]>([]);
const $upsellCategories = createStore<IPromptsLibraryCategory[]>([]);
const $subcategories = createStore<IPromptsLibrarySubcategory[]>([]);
const $prompts = createStore<IPrompt[]>([]);

const getAllCategoriesFx = createEffect(async () => {
    const response = await PromptsLibraryApi.getAllCategories();
    return response;
});

const getSubcategoriesByCategoryIdFx = createEffect(async (categoryId: number) => {
    const response = await PromptsLibraryApi.getSubcategoriesByCategoryId(categoryId);
    return response;
});

const getPromptsByCategoryIdFx = createEffect(async (categoryId: number) => {
    const response = await PromptsLibraryApi.getPromptsByCategoryId(categoryId);
    return response;
});

sample({
    clock: getAllCategoriesFx.doneData,
    target: $allCategories
});

sample({
    clock: getSubcategoriesByCategoryIdFx.doneData,
    target: $subcategories
});

sample({
    clock: getPromptsByCategoryIdFx.doneData,
    target: $prompts
});

const contract = new ModelContract(
    { $allCategories, $upsellCategories, $subcategories, $prompts },
    {},
    { getSubcategoriesByCategoryIdFx, getPromptsByCategoryIdFx },
    {}
);

export type IPromptsLibraryContract = typeof contract;

export class PromptsLibraryModule extends ModuleDescription<IPromptsLibraryContract> {
    constructor(
        public routes: IAppRouteUnitsContract,
        public app: IAppContract,
        public router: IRouterContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: [this.routes.stores.promptsLibrary.opened],
            source: { query: this.routes.stores.promptsLibrary.$query },
            filter: (s) => s.query.pack === "ultimate_ai" || s.query.pack === "personal_boost",
            target: getAllCategoriesFx
        });

        sample({
            clock: [this.routes.stores.promptsLibrary.opened],
            source: { query: this.routes.stores.promptsLibrary.$query, path: this.routes.stores.promptsLibrary.$path },
            filter: (s) => {
                if (s.path?.startsWith("/prompts-library/claude_pack")) return false;
                return !(s.query.pack === "ultimate_ai" || s.query.pack === "personal_boost");
            },
            fn: () => `/home`,
            target: this.router.events.routerReplace
        });

        sample({
            source: { params: this.routes.stores.promptsLibrary.$params, categories: $allCategories },
            filter: (s) => {
                if (!Boolean(s.params.categoryId) || s.categories.length === 0) return false;

                const categoryById = s.categories.find((c) => c.id === Number(s.params.categoryId));
                return Boolean(categoryById?.has_subcategories);
            },
            fn: (s) => Number(s.params.categoryId),
            target: getSubcategoriesByCategoryIdFx
        });

        sample({
            source: { params: this.routes.stores.promptsLibrary.$params, categories: $allCategories },
            filter: (s) => {
                if (Boolean(s.params.subcategoryId) || !Boolean(s.params.categoryId) || s.categories.length === 0)
                    return false;
                const categoryById = s.categories.find((c) => c.id === Number(s.params.categoryId));

                return !Boolean(categoryById?.has_subcategories);
            },
            fn: (s) => Number(s.params.categoryId),
            target: getPromptsByCategoryIdFx
        });

        sample({
            source: this.routes.stores.promptsLibrary.$params,
            filter: (s) => Boolean(s.subcategoryId),
            fn: (s) => Number(s.subcategoryId),
            target: getPromptsByCategoryIdFx
        });

        sample({
            source: { query: this.routes.stores.promptsLibrary.$query, allCategories: $allCategories },
            filter: (s) => {
                if (s.allCategories.length === 0) return false;
                const pack = s.query.pack;
                return pack === "ultimate_ai" || pack === "personal_boost";
            },
            fn: (s) => {
                const pack = s.query.pack;
                const packCategoryIds = pack === "ultimate_ai" ? ULTIMATE_AI_CATEGORY_IDS : PERSONAL_BOOST_CATEGORY_IDS;
                return s.allCategories.filter((category) => packCategoryIds.includes(category.id));
            },
            target: $upsellCategories
        });

        reset({
            clock: this.app.events.routePathNameChanged,
            target: [$subcategories, $prompts]
        });
    }
}
